import React, { useState, useEffect, useCallback } from 'react';
import { fetchStandards, fetchGradeLevels, fetchDomains, fetchSubjects } from '../../api/standards';
import styles from './StandardsSection.module.css';

export default function StandardsSection({ lessonId, standards = [], onAdd, onRemove, gradeLevel, subject }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(subject || '');
  const [selectedGrade, setSelectedGrade] = useState(gradeLevel || '');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load subjects when picker opens
  useEffect(() => {
    if (!pickerOpen) return;
    fetchSubjects()
      .then(res => setSubjects(res.data || []))
      .catch(() => {});
  }, [pickerOpen]);

  // Load grade levels when picker opens or subject changes
  useEffect(() => {
    if (!pickerOpen) return;
    fetchGradeLevels(selectedSubject || undefined)
      .then(res => setGrades(res.data || []))
      .catch(() => {});
  }, [pickerOpen, selectedSubject]);

  // Load domains when grade or subject changes
  useEffect(() => {
    if (!pickerOpen) return;
    fetchDomains(selectedGrade || undefined, selectedSubject || undefined)
      .then(res => setDomains(res.data || []))
      .catch(() => {});
  }, [pickerOpen, selectedGrade, selectedSubject]);

  // Search standards
  const searchStandards = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedSubject) params.subject = selectedSubject;
      if (selectedGrade) params.grade = selectedGrade;
      if (selectedDomain) params.domain = selectedDomain;
      if (searchText.trim()) params.search = searchText.trim();
      const res = await fetchStandards(params);
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSubject, selectedGrade, selectedDomain, searchText]);

  // Auto-search when filters change
  useEffect(() => {
    if (!pickerOpen) return;
    const timer = setTimeout(searchStandards, 300);
    return () => clearTimeout(timer);
  }, [pickerOpen, selectedSubject, selectedGrade, selectedDomain, searchText, searchStandards]);

  const linkedIds = new Set(standards.map(s => s.standard?.id || s.standardId));

  const handleAdd = (standard) => {
    if (onAdd) onAdd(standard);
  };

  const handleRemove = (standardId) => {
    if (onRemove) onRemove(standardId);
  };

  return (
    <div className="document-section">
      <h3 className="section-title">Standards Alignment</h3>

      {standards.length === 0 && !pickerOpen && (
        <p className="section-placeholder">No standards linked yet. Click below to add standards.</p>
      )}

      {standards.length > 0 && (
        <div className={styles.linkedStandards}>
          {standards.map((ls) => {
            const std = ls.standard || ls;
            return (
              <div key={std.id} className={styles.standardChip}>
                <span className={styles.chipCode}>{std.standardCode}</span>
                <span className={styles.chipDesc}>{std.description}</span>
                {ls.isPrimary && <span className={styles.primaryBadge}>Primary</span>}
                <button
                  className={`${styles.chipRemove} no-print`}
                  onClick={() => handleRemove(std.id)}
                  title="Remove standard"
                >
                  &times;
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!pickerOpen ? (
        <button
          className="add-item-btn no-print"
          onClick={() => setPickerOpen(true)}
        >
          + Add Standard
        </button>
      ) : (
        <div className={`${styles.picker} no-print`}>
          <div className={styles.pickerHeader}>
            <h4 className={styles.pickerTitle}>Browse Standards</h4>
            <button
              className={styles.pickerClose}
              onClick={() => setPickerOpen(false)}
            >
              &times;
            </button>
          </div>

          <div className={styles.filters}>
            <select
              value={selectedSubject}
              onChange={(e) => { setSelectedSubject(e.target.value); setSelectedGrade(''); setSelectedDomain(''); }}
              className={styles.filterSelect}
            >
              <option value="">All Subjects</option>
              {subjects.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select
              value={selectedGrade}
              onChange={(e) => { setSelectedGrade(e.target.value); setSelectedDomain(''); }}
              className={styles.filterSelect}
            >
              <option value="">All Grades</option>
              {grades.map(g => (
                <option key={g} value={g}>{g === 'K' ? 'Kindergarten' : g.includes('-') || g === 'MS' || g === 'HS' ? g : `Grade ${g}`}</option>
              ))}
            </select>

            <select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">All Domains</option>
              {domains.map(d => (
                <option key={d.domainCode} value={d.domainCode}>
                  {d.domainCode} - {d.domainName}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search standards..."
              className={styles.searchInput}
            />
          </div>

          <div className={styles.resultsList}>
            {loading && <p className={styles.loadingText}>Searching...</p>}
            {!loading && results.length === 0 && (
              <p className={styles.emptyText}>No standards found. Try adjusting filters.</p>
            )}
            {!loading && results.map(std => {
              const isLinked = linkedIds.has(std.id);
              return (
                <div
                  key={std.id}
                  className={`${styles.resultItem} ${isLinked ? styles.resultLinked : ''}`}
                  onClick={() => !isLinked && handleAdd(std)}
                >
                  <span className={styles.resultCode}>{std.standardCode}</span>
                  <span className={styles.resultDesc}>{std.description}</span>
                  {isLinked && <span className={styles.linkedLabel}>Added</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
